import { reactive } from 'vue';
import { defineStore } from 'pinia';
import axios, { AxiosError } from 'axios';

import {
  CoordinateType,
  SatelliteApiResType,
  SatelliteDataType,
  SatelliteInfoType,
  TrackingTableType,
} from '@/interface/map';
import router from '@/router';
import useCommonStore from '@/store/storeCommon';
import useMemberStore from '@/store/storeMember';

const useMapStore = defineStore('mapStore', () => {
  const { accessedMemberInfo, accessLogout } = useMemberStore();
  const { modalContents } = useCommonStore();

  const satelliteInfo = reactive({
    locationName: '', // 위성 위치 정보
    isCurrentTrackingSat: false, // 위성 Tracking 여부
  });

  // 위성 정보
  const satelliteBasicData: SatelliteInfoType = reactive({
    name: '',
    coordinate: {
      lat: '',
      lon: '',
    },
  });

  // 위성 위치
  const satelliteTleData: SatelliteDataType = reactive({
    tle: '',
    expTime: '',
    trackingTables: [],
  });

  const changeCurrentTrackingSat = (): void => {
    satelliteInfo.isCurrentTrackingSat = !satelliteInfo.isCurrentTrackingSat;
  };

  /**
   * 위성명을 담는 함수
   */
  const changeSatelliteName = (data: string): void => {
    satelliteBasicData.name = data;
  };

  /**
   * 위성 좌표를 담는 함수
   */
  const changeCoordinate = (data: CoordinateType): void => {
    const { lat, lon } = data;

    satelliteBasicData.coordinate.lat = lat;
    satelliteBasicData.coordinate.lon = lon;
  };

  /**
   * 위성 위치를 가져오는 함수
   */
  const getSatelliteDataApi = async (): Promise<void> => {
    Object.assign(satelliteTleData, {
      tle: '',
      expTime: '',
      trackingTables: [],
    });

    const localToken: string | null = localStorage.getItem('accessToken'); // localStorage에 저장된 accessToken을 가져옵니다.
    const sessionToken: string | null = sessionStorage.getItem('accessToken'); // sessionStorage에 저장된 accessToken을 가져옵니다.

    const configHeaders = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localToken ? localToken : sessionToken,
      },
    };

    // accessToken을 받으면 로그인한 유저정보를 받아옵니다.
    await axios.get<SatelliteApiResType>('/api/satellite', configHeaders)
      .then(({ data }): void => {
        const { name, nickname, email, level, award, tle, expTime, trackingTables } = data;

        if (data.name) {
          Object.assign(accessedMemberInfo, {
            name: name,
            nickname: nickname,
            email: email,
            level: level,
            award: award,
          });
        }

        Object.assign(satelliteTleData, {
          tle: tle,
          expTime: expTime,
          trackingTables: trackingTables,
        });

        if (!satelliteTleData.trackingTables) {
          Object.assign(modalContents, {
            title: 'Error',
            description: 'Failed to get Tracking Table.\nPlease refresh.',
          });
        }
      })
      .catch((e: AxiosError): void => {
        console.log(e);
        if (e.response!.status === 401) {
          accessLogout();
          router.go(0);
        }
      });
  };

  /**
   * 좌표정보를 가져옵니다.
   * @param flag
   */
  const getCoordinate = (flag: string): string => {
    if (flag === 'Lat') {
      return satelliteBasicData.coordinate.lat;
    } else {
      return satelliteBasicData.coordinate.lon;
    }
  };

  /**
   * 위성 이름을 가져옵니다.(로그인시 로그인한 유저의 이름)
   */
  const getSatelliteName = (): string => {
    if (accessedMemberInfo.nickname) {
      return accessedMemberInfo.nickname;
    } else if (accessedMemberInfo.name) {
      return accessedMemberInfo.name;
    } else {
      return satelliteBasicData.name;
    }
  };

  /**
   * Tracking Table의 현재 시간에 해당하는 Country이름을 찾아옵니다.
   */
  const getCountry = (): void => {
    const date = new Date().toISOString().split('.')[0];

    if (satelliteTleData.trackingTables) {
      const findIndex = satelliteTleData.trackingTables.findIndex((trackingTable: TrackingTableType) => {
        return trackingTable.startTime <= date && date <= trackingTable.endTime;
      });
      satelliteInfo.locationName = findIndex !== -1 ? satelliteTleData.trackingTables[findIndex].country : '';
    }
  };

  /**
   * Expire Time이 도래하였을 때 위성 정보 조회 API를 다시 실행시킵니다.
   */
  const executeApiOnSchedule = async (): Promise<void> => {
    const date = new Date().toISOString().split('.')[0];

    if (satelliteTleData.expTime <= date) {
      await getSatelliteDataApi();
    }
  };

  return {
    satelliteBasicData,
    satelliteTleData,
    satelliteInfo,
    changeCurrentTrackingSat,
    changeSatelliteName,
    changeCoordinate,
    getSatelliteDataApi,
    getCoordinate,
    getSatelliteName,
    getCountry,
    executeApiOnSchedule,
  };
});

export default useMapStore;
